import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { addDays } from "date-fns";

import SectionTitle from "../SectionTitle/SectionTitle";
import Switch from "../Switch/Switch";
import CustomDatePicker from "../CustomDatePicker/CustomDatePicker";
import { Clock, Check } from "lucide-react";
import { toastService } from "../../services/ToastService";
import SalesAPI, { Sale } from "../../api/SalesAPI";

export type OpinionSchedulingIntent = {
  enabled: boolean;
  date: Date | null;
};

type Props = {
  sale: Sale;
  onSaleChange: (value: Sale) => void;
  isDisabled?: boolean;
  /** Latest enable + date for parent-driven finalize (e.g. register sale + schedule in one flow). */
  onSchedulingIntentChange?: (intent: OpinionSchedulingIntent) => void;
};

export function SaleOpinionSection({
  sale,
  onSaleChange,
  isDisabled = false,
  onSchedulingIntentChange,
}: Props) {
  const { t } = useTranslation();
  const settings = useSelector((state: any) => state.settings.value);

  const [opinionScheduledDate, setOpinionScheduledDate] = useState<Date | null>(null);
  const [, setIsSchedulingOpinion] = useState(false);
  const [, setIsDeletingOpinion] = useState(false);
  const [isOpinionRequestEnabled, setIsOpinionRequestEnabled] = useState(false);

  const isOpinionRequestAlreadySent = (currentSale: Sale | null): boolean => {
    if (!currentSale?.opinionRequestScheduledDate) {
      return false;
    }
    const scheduledDate = new Date(currentSale.opinionRequestScheduledDate);
    const now = new Date();
    return now > scheduledDate;
  };

  // Initialize scheduled date and enabled state from sale
  useEffect(() => {
    if (sale.opinionRequestScheduledDate) {
      setOpinionScheduledDate(new Date(sale.opinionRequestScheduledDate));
      setIsOpinionRequestEnabled(true);
    } else if (sale.weddingDate && !isOpinionRequestAlreadySent(sale)) {
      // Default: 3 days after wedding date
      const weddingDate = new Date(sale.weddingDate);
      const defaultDate = addDays(weddingDate, 3);
      setOpinionScheduledDate(defaultDate);
      setIsOpinionRequestEnabled(false);
    } else {
      setOpinionScheduledDate(null);
      setIsOpinionRequestEnabled(false);
    }
  }, [sale]);

  useEffect(() => {
    if (!onSchedulingIntentChange) return;
    onSchedulingIntentChange({
      enabled: isOpinionRequestEnabled || !!sale.opinionRequestScheduledDate,
      date: opinionScheduledDate,
    });
  }, [
    onSchedulingIntentChange,
    isOpinionRequestEnabled,
    opinionScheduledDate,
    sale.opinionRequestScheduledDate,
  ]);

  const handleScheduleOpinionRequest = useCallback(
    async (scheduledDate?: Date) => {
      if (!sale.id) {
        toastService.showToast(
          t("sales.form.opinion.sale_required") || "Sale must be saved first",
          "error"
        );
        return;
      }

      if (isOpinionRequestAlreadySent(sale)) {
        toastService.showToast(
          t("sales.form.opinion.already_sent") || "Opinion request has already been sent",
          "error"
        );
        return;
      }

      setIsSchedulingOpinion(true);
      try {
        const dateToSchedule = scheduledDate || opinionScheduledDate;
        if (!dateToSchedule) {
          toastService.showToast(
            t("sales.form.opinion.date_required") || "Please select a date",
            "error"
          );
          return;
        }

        const response = await SalesAPI.scheduleOpinionRequest(
          sale.id,
          dateToSchedule.toISOString()
        );

        toastService.showToast(
          t("sales.form.opinion.scheduled_success") || "Opinion request scheduled successfully",
          "success"
        );

        onSaleChange(response.data);
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          t("sales.form.opinion.schedule_error") ||
          "Failed to schedule opinion request";
        toastService.showToast(errorMessage, "error");
      } finally {
        setIsSchedulingOpinion(false);
      }
    },
    [sale, opinionScheduledDate, onSaleChange, t]
  );

  const handleDeleteOpinionRequest = useCallback(async () => {
    if (!sale.id) {
      return;
    }

    if (isOpinionRequestAlreadySent(sale)) {
      toastService.showToast(
        t("sales.form.opinion.cannot_delete_sent") ||
          "Cannot delete opinion request that has already been sent",
        "error"
      );
      return;
    }

    setIsDeletingOpinion(true);
    try {
      const response = await SalesAPI.deleteOpinionRequest(sale.id);

      toastService.showToast(
        t("sales.form.opinion.deleted_success") || "Opinion request deleted successfully",
        "success"
      );

      setIsOpinionRequestEnabled(false);
      onSaleChange(response.data);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        t("sales.form.opinion.delete_error") ||
        "Failed to delete opinion request";
      toastService.showToast(errorMessage, "error");
    } finally {
      setIsDeletingOpinion(false);
    }
  }, [sale, onSaleChange, t]);

  const localSale = sale;

  // Already sent banner
  if (isOpinionRequestAlreadySent(localSale)) {
    return (
      <div className="space-y-5">
        <SectionTitle text={t("sales.form.opinion.title") || "Opinion Request"} />
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <Check className="h-6 w-6 text-green-600" />
            <div>
              <p className="font-medium text-green-900">
                {t("sales.form.opinion.sent") || "Opinion request has been sent"}
              </p>
              {localSale.opinionRequestScheduledDate && (
                <p className="text-sm text-green-700 mt-1">
                  {t("sales.form.opinion.sent_on") || "Sent on"}:{" "}
                  {new Date(localSale.opinionRequestScheduledDate).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <SectionTitle text={t("sales.form.opinion.title") || "Opinion Request"} />

      <div className="space-y-4">
        {/* Switch to enable/disable opinion request */}
        <div className="flex items-center space-x-3">
          <Switch
            checked={isOpinionRequestEnabled || !!localSale?.opinionRequestScheduledDate}
            onChange={() => {
              const enabled = !(isOpinionRequestEnabled || !!localSale?.opinionRequestScheduledDate);
              setIsOpinionRequestEnabled(enabled);

              if (!enabled && localSale?.opinionRequestScheduleId) {
                // If disabling and there's a scheduled request, delete it
                handleDeleteOpinionRequest();
              } else if (
                enabled &&
                localSale?.weddingDate &&
                !localSale?.opinionRequestScheduledDate
              ) {
                const weddingDate = new Date(localSale.weddingDate);
                const recommendedDate = addDays(weddingDate, 3);
                setOpinionScheduledDate(recommendedDate);
                if (localSale?.id) {
                  handleScheduleOpinionRequest(recommendedDate);
                }
              }
            }}
            disabled={
              isDisabled ||
              isOpinionRequestAlreadySent(localSale) ||
              settings?.weddingRateReminderEnabled === false
            }
          />
          <label
            className="text-sm font-medium cursor-pointer"
            onClick={() => {
              if (
                !isDisabled &&
                !isOpinionRequestAlreadySent(localSale) &&
                settings?.weddingRateReminderEnabled !== false
              ) {
                const enabled = !(
                  isOpinionRequestEnabled || !!localSale?.opinionRequestScheduledDate
                );
                setIsOpinionRequestEnabled(enabled);
                if (!enabled && localSale?.opinionRequestScheduleId) {
                  handleDeleteOpinionRequest();
                } else if (
                  enabled &&
                  localSale?.weddingDate &&
                  !localSale?.opinionRequestScheduledDate
                ) {
                  const weddingDate = new Date(localSale.weddingDate);
                  const recommendedDate = addDays(weddingDate, 3);
                  setOpinionScheduledDate(recommendedDate);
                  if (localSale?.id) {
                    handleScheduleOpinionRequest(recommendedDate);
                  }
                }
              }
            }}
          >
            {t("sales.form.opinion.enable") || "Enable Opinion Request"}
          </label>
        </div>

        {settings?.weddingRateReminderEnabled === false && (
          <div className="mt-2">
            <p className="text-xs text-gray-500">
              {t("sales.form.opinion.reminder_disabled") ||
                "Opinion reminder is disabled in Settings > Notifications"}
            </p>
          </div>
        )}

        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-800">
            {t("sales.form.opinion_reminder.settings_note.before") ||
              "Note: You can modify the email template and reminder settings in the "}
            <a
              href="/settings?activeTab=notifications"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-700 font-medium underline underline-offset-2 hover:text-blue-900"
            >
              {(t("sales.form.opinion_reminder.settings_note.link") || "Settings > Notifications").trim() || "Settings > Notifications"}
            </a>
            {t("sales.form.opinion_reminder.settings_note.after") || " section."}
          </p>
        </div>

        {/* Scheduled notification (read-only summary) */}
        {localSale?.opinionRequestScheduledDate && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <Clock className="h-5 w-5 text-blue-600" />
              <div>
                <p className="font-medium text-blue-900">
                  {t("sales.form.opinion.scheduled") || "Opinion request scheduled"}
                </p>
                <p className="text-sm text-blue-700 mt-1">
                  {t("sales.form.opinion.scheduled_for") || "Scheduled for"}:{" "}
                  {new Date(localSale.opinionRequestScheduledDate).toLocaleDateString()}
                </p>
                {localSale.weddingDate && (
                  <p className="text-sm text-blue-600 mt-1">
                    {t("sales.form.opinion.wedding_date_note", {
                      weddingDate: new Date(localSale.weddingDate).toLocaleDateString(),
                      recommendedDate: addDays(new Date(localSale.weddingDate), 3).toLocaleDateString(),
                    })}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Date picker when enabled */}
        {(isOpinionRequestEnabled || !!localSale?.opinionRequestScheduledDate) && (
          <div>
            <CustomDatePicker
              label={t("sales.form.opinion.schedule_date") || "Schedule Date"}
              value={
                opinionScheduledDate ||
                (localSale?.opinionRequestScheduledDate
                  ? new Date(localSale.opinionRequestScheduledDate)
                  : undefined)
              }
              onChange={(date) => {
                setOpinionScheduledDate(date || null);
                if (date && localSale?.id) {
                  // Auto-schedule/reschedule when date changes
                  handleScheduleOpinionRequest(date);
                }
              }}
              isDisabled={isDisabled || isOpinionRequestAlreadySent(localSale)}
              timeZone={settings.timezone}
            />
          </div>
        )}
      </div>
    </div>
  );
}

